import { ColumnDef } from '@tanstack/react-table';

import { humanizeString } from 'support/helpers/generic/generic';
import { WebEDIRowData } from 'components/DataTable/Types';

import { extractParentKey, isEmbeddedTableFlatKey } from '../pathsUtils';

export const MAX_COLUMNS_TO_DISPLAY = 4;

export type Column = ColumnDef<WebEDIRowData, unknown>;

export type UploadErrorWithColumns = {
  columns?: Array<string>;
};

export type FormattedColumnsResult = {
  columns: string;
  lastColumn: string;
};

const getSingleColumnLabel = (column: Column | undefined, id: string): string => {
  return column?.meta?.label || humanizeString(id);
};

const getEmbeddedTableColumnLabel = (parentId: string, childKey: string, columns: Array<Column>): string | null => {
  const parentColumn = columns.find((col) => col.id === parentId);

  if (!parentColumn || !('columns' in parentColumn) || !Array.isArray(parentColumn.columns)) {
    return null;
  }

  const parentLabel = getSingleColumnLabel(parentColumn, parentId);
  const childColumn = parentColumn.columns.find((col) => col.id === childKey);
  const childLabel = childColumn?.meta?.label;
  // Return combined label (Parent - Child) for clarity in error messages
  // Example: "Packaging Units - Length" instead of just "Length"
  // This helps users identify which nested table the column belongs to
  return childLabel
    ? `${parentLabel} - ${childLabel}`
    : humanizeString(childKey.replace(/\./g, '_').replaceAll('*', '-'));
};

/**
 * Gets the display label for a column, preferring meta.label over id
 * For embedded table columns (e.g., "items.*.name"), returns "Parent Label - Child Label"
 */
export const getColumnLabel = (columnId: string, columns: Array<Column>): string => {
  if (isEmbeddedTableFlatKey(columnId)) {
    const parentId = extractParentKey(columnId);
    const embeddedTableColumnLabel = getEmbeddedTableColumnLabel(parentId, columnId, columns);
    if (embeddedTableColumnLabel) {
      return embeddedTableColumnLabel;
    }
  }

  const column = columns.find((col) => col.id === columnId);
  return getSingleColumnLabel(column, columnId);
};

/**
 * Formats column names for error display
 *
 * @param uploadError - Error object that may contain column IDs
 * @param columns - Available column definitions
 * @param maxColumns - Maximum number of columns to display (defaults to MAX_COLUMNS_TO_DISPLAY)
 * @returns Object with formatted columns string and lastColumn string
 *
 * @example
 * // With few columns
 * formatErrorColumns({ columns: ['col1', 'col2'] }, [...], 4)
 * // Returns: { columns: '"Column 1"', lastColumn: '"Column 2"' }
 *
 * @example
 * // With many columns
 * formatErrorColumns({ columns: ['col1', 'col2', 'col3', 'col4', 'col5'] }, [...], 4)
 * // Returns: { columns: '"Column 1", "Column 2", "Column 3", "Column 4"', lastColumn: '' }
 */
export type ErrorWithPossibleColumns = {
  columns?: Array<string>;
  code?: string;
};

export const formatErrorColumns = (
  uploadError: ErrorWithPossibleColumns,
  columns: Array<Column>,
  maxColumns: number = MAX_COLUMNS_TO_DISPLAY,
): FormattedColumnsResult => {
  // Return empty result if no columns data
  if (!uploadError.columns?.length) {
    return { columns: '', lastColumn: '' };
  }

  const errorColumns = uploadError.columns;
  const hasTooManyColumns = errorColumns.length > maxColumns;

  // If too many columns, show only the first N columns
  if (hasTooManyColumns) {
    const displayColumns = errorColumns
      .slice(0, maxColumns)
      .map((columnId) => `"${getColumnLabel(columnId, columns)}"`)
      .join(', ');
    return { columns: displayColumns, lastColumn: '' };
  }

  // Format all columns with proper separation for translation
  const allColumns = errorColumns.map((columnId) => `"${getColumnLabel(columnId, columns)}"`);

  // Single column case
  if (allColumns.length <= 1) {
    return { columns: allColumns[0] || '', lastColumn: '' };
  }

  // Multiple columns: separate last column for proper grammar in translations
  return {
    columns: allColumns.slice(0, -1).join(', '),
    lastColumn: allColumns[allColumns.length - 1],
  };
};
